import {Livello, Soggetto, Workflow} from "./Workflow";
import {LavoratoreBase} from "@commons/mapping/LavoratoreBase";
import {URIAndTrace} from "@commons/mapping/URIAndTrace";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {UserCRUDRecord} from "@commons/mapping/UserCRUDRecord";
import {CausaleWFRecord, TipoCausaleWF} from "@commons/mapping/CausaleWFRecord";
import {FPNetDate, ObjectFactory} from "@commons/types";
import {Coppia} from "@commons/mapping/Calc";
import {GiustificativoAggancioRecord} from "@commons/mapping/GiustificativoAggancioRecord";
import {merge} from "lodash";

export interface PresenzeCausaleWFBase {
    wf: Workflow,
    idTabella: number, // Id tabella di workflow
    codice?: string, // Codice causale di workflow originale (inserita dal richiedente)
    codiceHR?: string, // Eventuale codice causale di workflow modificata dall' approvatore o dal responsabile HR
    codiceGiustificativo?: string,
    descrizioneCausale?: string,
    descrizioneGiustificativo?: string,
    coloreCausale?: string,
    tipoCausale?: TipoCausaleWF,
    quantita: number,
    quantitaGiustificativo: number,
    giornoDt: FPNetDate,
    giornoInizio: string,
    giornoFine: string,
    giornataIntera: boolean,
    oraInizio?: string,
    oraFine?: string,
    elaborato: boolean, // se true, la causale è stata caricata in presenze
    anomalia: boolean,
    causaleWF: CausaleWFRecord | null,// Solo per gestione presenze
    aggancio: GiustificativoAggancioRecord | null, // Solo per gestione presenze
    jsonTimbrature?: string,
    effettiveOriginaliBackup?: string[]
}

export interface PresenzeCausaleWFRecord extends LavoratoreBase, PresenzeCausaleWFBase, URIAndTrace {
}

export class PresenzeCausaleWFRecord {
    static factory: ObjectFactory<PresenzeCausaleWFRecord> = (json?) => {
        return merge<PresenzeCausaleWFRecord, Partial<PresenzeCausaleWFRecord> | undefined>({
            idAziendaMaster: 0,
            idAzienda: 0,
            id: 0,
            nome: null,
            cognome: null,
            descrizione: null,
            codiceFiscale: '',
            matricola: null,
            countXUL: 0,
            wf: Workflow.getInstance(),
            idTabella: 0,
            codice: '',
            codiceHR: '',
            //descrizioneCausale: '',
            //coloreCausale: '',
            //tipoCausale: 'ORE_MANCANTI',
            quantita: 0,
            quantitaGiustificativo: 0,
            giornoDt: '',
            giornoInizio: '',
            giornoFine: '',
            giornataIntera: false,
            oraInizio: '',
            oraFine: '',
            elaborato: false,
            anomalia: false,
            causaleWF: null,
            aggancio: null,
            URI: null,
        }, json);
    }

    static getFilledInstance = (lavoratore: LavoratoreBase, day: PresenzeGiornataPlusRecord, user: UserCRUDRecord) => {
        const resource = PresenzeCausaleWFRecord.factory();
        resource.id = lavoratore.id;
        resource.idAzienda = lavoratore.idAzienda;
        resource.idAziendaMaster = lavoratore.idAziendaMaster;
        resource.codiceFiscale = lavoratore.codiceFiscale;
        resource.descrizione = lavoratore.cognome + ' ' + lavoratore.nome;

        resource.giornoDt = resource.giornoInizio = resource.giornoFine = day.dtGiornata!;
        resource.wf = Workflow.getInstance();

        if (!resource.wf.livelli || resource.wf.livelli.length === 0) {
            resource.wf.livelli = [];
            resource.wf.livelli.push(Livello.getInstance());
        }
        const livello0 = resource.wf.livelli[0];
        livello0.richiedente = Soggetto.getInstance();
        livello0.richiedente.idUser = user!.idUser;
        livello0.richiedente.userName = user!.userName;

        //console.log('lavoratore.codiceFiscale === user!.codiceFiscale', lavoratore.codiceFiscale === user!.codiceFiscale)
        if (lavoratore.codiceFiscale === user!.codiceFiscale) {
            resource.wf.stato = 'IN_ATTESA';
            livello0.stato = 'IN_ATTESA';
        } else {
            resource.wf.stato = 'IN_VALUTAZIONE';
            livello0.stato = 'IN_VALUTAZIONE';

            livello0.approvatore = Soggetto.getInstance();
            livello0.approvatore.idUser = user!.idUser;
            livello0.approvatore.userName = user!.userName;
            //livello0.approvatore.note = 'ciao';
        }

        return resource;
    }

    static isDeleted = function (presenzeCausale: PresenzeCausaleWFRecord) {
        return Workflow.isDeleted(presenzeCausale.wf);
    };

    static getDeleteTrace = function (presenzeCausale: PresenzeCausaleWFRecord) {
        return presenzeCausale.wf.deleteTrace;
    };

    static getNoteRichiedente = function (presenzeCausale: PresenzeCausaleWFRecord): string | null {
        if (presenzeCausale.wf && presenzeCausale.wf.livelli && presenzeCausale.wf.livelli.length > 0) {
            return presenzeCausale.wf.livelli[0].richiedente !== null ? presenzeCausale.wf.livelli[0].richiedente.note : '';
        }
        return null;
    };

    static getNoteApprovatore = function (presenzeCausale: PresenzeCausaleWFRecord): string | null {
        if (presenzeCausale.wf && presenzeCausale.wf.livelli && presenzeCausale.wf.livelli.length > 0) {
            return presenzeCausale.wf.livelli[0].approvatore !== null ? presenzeCausale.wf.livelli[0].approvatore.note : '';
        }
        return null;
    };

    static getDescrizioneCausale = function (presenzeCausale: Pick<PresenzeCausaleWFRecord, 'descrizioneCausale' | 'codice' | 'codiceHR'>, usaCodiceHR: boolean = false) {
        return presenzeCausale.descrizioneCausale + ' [' + (!usaCodiceHR || presenzeCausale.codiceHR?.length === 0 ? presenzeCausale.codice : presenzeCausale.codice + ' -> ' + presenzeCausale.codiceHR) + ']';
    };

    static getCoppia(that: PresenzeCausaleWFRecord) {
        if (that.giornataIntera) {
            return null;
        } else {
            return new Coppia(parseInt(that.oraInizio!), parseInt(that.oraFine!));
        }
    }

    static getDescrizioneQuantita = (presenzeCausale: PresenzeCausaleWFRecord) => {
        const HHmmFormat = (time: string) => time.slice(0, 2) + ':' + time.slice(2);
        const tipoCausale = presenzeCausale.causaleWF?.tipoCausale || presenzeCausale.tipoCausale;
        //console.log('tipoCausale', tipoCausale);
        if (tipoCausale === 'MANUTENZIONE_TIMBRATURE')
            return '';
        else if (tipoCausale === 'QUANTITA')
            return presenzeCausale.quantita;
        else {
            if (presenzeCausale.giornataIntera)
                return 'Intera giornata';
            else {
                if (presenzeCausale.oraInizio === presenzeCausale.oraFine) {
                    return presenzeCausale.quantita.toFixed(2) + ' hh';
                } else {
                    return `${HHmmFormat(presenzeCausale.oraInizio!)}-${HHmmFormat(presenzeCausale.oraFine!)}`
                }
            }
        }
    }

    static getKey = (that: PresenzeCausaleWFRecord) => {
        return that.wf.idWF + '_' + that.giornoDt!
    }
}